/**
 * EnvelopeDetailPage
 *
 * Owner-side post-send view. Shows status, recipient progress, and a
 * Download Sealed button that polls until the sealed PDF lands. Reseal
 * affordance surfaces only when the envelope is `completed` but no sealed
 * file exists — degraded state from a failed async seal after final submit.
 * Sent / in-progress envelopes get a Void button with a confirmation modal.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Ban, Download, KeyRound, RefreshCw, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/lib/api';
import { HolderSwitcher } from '@/components/HolderSwitcher';
import { ArcheHeader } from '../../../components/ArcheHeader';
import {
  useEnvelope,
  useEnvelopeEvents,
  useResealEnvelope,
  useResetRecipientAccessCode,
  useVoidEnvelope,
} from '../hooks';
import { useSelectedHolder } from '../hooks/useSelectedHolder';
import { useOwnerSealedDownload } from '../hooks/useSealedDownload';
import { AuditTrail } from '../components/AuditTrail';

/**
 * Whether a recipient is still waiting on a signing email that never went out.
 *
 * There is no resend (the raw signing link exists only while the email is
 * built), so that signer has no link and cannot know it — and on a sequential
 * envelope everyone after them waits. The owner is the recovery path, and
 * `signingEmailFailedAt` is the durable record of it: the warning email to the
 * owner is best-effort and rides the channel that just failed. Irrelevant once
 * the signer has acted or the envelope has ended.
 */
function signingEmailUndelivered(
  envelopeStatus: string,
  r: { signingEmailFailedAt?: string | null; status: string },
): boolean {
  return (
    !!r.signingEmailFailedAt &&
    (envelopeStatus === 'sent' || envelopeStatus === 'in_progress') &&
    r.status !== 'signed' &&
    r.status !== 'declined' &&
    r.status !== 'declined_electronic'
  );
}

export function EnvelopeDetailPage() {
  const { envelopeId } = useParams<{ envelopeId: string }>();
  const navigate = useNavigate();
  const { envelope, loading, refresh } = useEnvelope(envelopeId);
  const sealed = useOwnerSealedDownload(envelopeId, {
    // Poll only while the envelope is completed but the sealed link is
    // still 404. Once we have a 200, stop refetching.
    refetchInterval: envelope?.status === 'completed' ? 5_000 : false,
  });
  const reseal = useResealEnvelope(envelopeId ?? '');
  const voidMutation = useVoidEnvelope(envelopeId ?? '');
  const eventsQuery = useEnvelopeEvents(envelopeId);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  // Post-send access-code reset: the recovery path for a signer who is locked
  // out or never received the code. One recipient at a time.
  const resetCode = useResetRecipientAccessCode(envelopeId ?? '');
  const [resetFor, setResetFor] = useState<string | null>(null);
  const [newCode, setNewCode] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);

  // The global workspace chip shows the user's active workspace preference.
  // The envelope itself is pinned to its create-time holder; switching the
  // chip on this page updates the preference for OTHER work but doesn't
  // affect what's displayed here (this page is keyed by envelopeId).
  const { holderId: activeHolderId, setHolderId: setActiveHolderId } = useSelectedHolder();

  if (!envelopeId) return null;

  if (loading || !envelope) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_document_sign" title="Document Sign">
          <HolderSwitcher selectedHolderId={activeHolderId} onChange={setActiveHolderId} />
        </ArcheHeader>
        <div className="max-w-3xl mx-auto p-6 w-full space-y-3">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  const sourceFiles = envelope.files.filter((f) => f.kind === 'source');
  const unreachedSigners = envelope.recipients.filter((r) =>
    signingEmailUndelivered(envelope.status, r),
  );
  const sealedDownload = sealed.data;
  const sealedNotReady = sealed.error instanceof ApiError && sealed.error.status === 404;
  const sealedFailed = envelope.status === 'completed' && sealedNotReady;

  async function handleReseal() {
    try {
      await reseal.mutateAsync();
      refresh();
      sealed.refetch();
    } catch {
      // Surfaced via reseal.error
    }
  }

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId="arche_document_sign" title="Document Sign">
        <HolderSwitcher selectedHolderId={activeHolderId} onChange={setActiveHolderId} />
        <Button variant="outline" size="sm" onClick={() => navigate('/a/arche_document_sign')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to envelopes
        </Button>
      </ArcheHeader>
      <div className="max-w-3xl mx-auto p-6 w-full space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold">{envelope.title}</h1>
            <p className="text-sm text-muted-foreground">
              Status: <span className="font-medium">{envelope.status.replace('_', ' ')}</span>
            </p>
            {envelope.status === 'voided' && envelope.voidedReason && (
              <p className="text-xs text-muted-foreground mt-1">
                Voided reason: {envelope.voidedReason}
              </p>
            )}
          </div>
          {(envelope.status === 'sent' || envelope.status === 'in_progress') && (
            <Button variant="outline" size="sm" onClick={() => setVoidOpen(true)}>
              <Ban className="h-4 w-4 mr-2" />
              Void
            </Button>
          )}
        </div>

        {envelope.status === 'draft' && (
          <div className="border rounded-md p-4 bg-muted/40 flex items-center justify-between gap-4">
            <p className="text-sm">
              This envelope is still a draft. Open the builder to add documents, recipients, and
              fields.
            </p>
            <Button onClick={() => navigate(`/a/arche_document_sign/envelope/${envelopeId}`)}>
              <Send className="h-4 w-4 mr-2" />
              Open builder
            </Button>
          </div>
        )}

        <section className="border rounded-md p-4 space-y-2">
          <h2 className="text-sm font-semibold">Recipients ({envelope.recipients.length})</h2>
          {envelope.recipients.length === 0 ? (
            <p className="text-sm text-muted-foreground">No recipients</p>
          ) : (
            <ul className="text-sm space-y-1">
              {envelope.recipients.map((r) => (
                <li key={r.recipientId} className="flex justify-between gap-3">
                  <span>
                    <strong>{r.name ?? r.email ?? 'Unnamed'}</strong>{' '}
                    {r.email && r.name && (
                      <span className="text-muted-foreground">({r.email})</span>
                    )}
                    <span className="text-xs text-muted-foreground ml-2">{r.role}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    {signingEmailUndelivered(envelope.status, r) && (
                      <span className="text-xs text-destructive">Signing email not sent</span>
                    )}
                    {/* Delivery status: only meaningful once an envelope has
                        sealed and the signer chose a Vault delivery holder. */}
                    {r.deliveredVaultDocumentId ? (
                      <span className="text-xs text-green-600">Copy delivered</span>
                    ) : (
                      r.deliveryHolderId && (
                        <span className="text-xs text-muted-foreground">Delivery pending</span>
                      )
                    )}
                    {/* Access-code state, and the one post-send edit the owner
                        can make to it. A locked signer has no other way back. */}
                    {r.requiresAccessCode &&
                      (envelope.status === 'sent' ||
                        envelope.status === 'in_progress' ||
                        // A viewer given a code who never entered it still
                        // needs it to reach the executed copy.
                        (envelope.status === 'completed' && !r.accessCodeVerifiedAt)) &&
                      r.status !== 'signed' &&
                      r.status !== 'declined' &&
                      r.status !== 'declined_electronic' && (
                        <>
                          {(r.accessCodeAttempts ?? 0) >= 5 && (
                            <span className="text-xs text-destructive">Code locked</span>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => {
                              setResetFor(r.recipientId);
                              setNewCode('');
                              setResetError(null);
                            }}
                          >
                            <KeyRound className="h-3 w-3 mr-1" />
                            Reset code
                          </Button>
                        </>
                      )}
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">
                      {r.status}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {unreachedSigners.length > 0 && (
            <p className="text-sm text-destructive" role="status">
              We couldn’t email{' '}
              {unreachedSigners.map((r) => r.name ?? r.email ?? 'a signer').join(', ')} their
              signing link, and a signing link can’t be resent yet. Contact{' '}
              {unreachedSigners.length === 1 ? 'them' : 'each of them'} directly — or void this
              envelope and send a new one.
            </p>
          )}
          {resetFor && (
            <form
              className="mt-3 rounded-md border bg-muted/30 p-3 space-y-2"
              onSubmit={async (e) => {
                e.preventDefault();
                setResetError(null);
                try {
                  await resetCode.mutateAsync({
                    recipientId: resetFor,
                    accessCode: newCode.trim(),
                  });
                  setResetFor(null);
                  setNewCode('');
                } catch (err) {
                  setResetError(err instanceof Error ? err.message : 'Could not reset the code');
                }
              }}
            >
              <Label htmlFor="ds-reset-access-code" className="text-xs">
                New access code for{' '}
                {envelope.recipients.find((r) => r.recipientId === resetFor)?.name ?? 'recipient'}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="ds-reset-access-code"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                  autoComplete="off"
                  minLength={4}
                  maxLength={64}
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={resetCode.isPending || newCode.trim().length < 4}
                >
                  {resetCode.isPending ? 'Saving…' : 'Set code'}
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setResetFor(null)}>
                  Cancel
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                This clears their failed attempts. Share the new code by phone or in person — never
                in email — and it cannot be viewed again once saved.
              </p>
              {resetError && <p className="text-xs text-destructive">{resetError}</p>}
            </form>
          )}
        </section>

        <section className="border rounded-md p-4 space-y-3">
          <h2 className="text-sm font-semibold">Documents</h2>
          {sourceFiles.length === 0 ? (
            <p className="text-sm text-muted-foreground">No source documents</p>
          ) : (
            <ul className="text-sm space-y-1">
              {sourceFiles.map((f) => (
                <li key={f.fileId} className="flex justify-between gap-3">
                  <span>
                    {f.fileName}
                    {f.pageCount !== null && (
                      <span className="text-muted-foreground"> ({f.pageCount} pages)</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="pt-2 border-t flex flex-wrap items-center gap-3">
            {envelope.status === 'completed' && sealedDownload && (
              <a
                href={sealedDownload.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium underline"
              >
                <Download className="h-4 w-4" />
                Download signed copy
              </a>
            )}
            {envelope.status === 'completed' && !sealedDownload && !sealedFailed && (
              <p className="text-sm text-muted-foreground">Preparing your sealed copy…</p>
            )}
            {sealedFailed && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-destructive">Sealing failed — try again.</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleReseal}
                  disabled={reseal.isPending}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {reseal.isPending ? 'Resealing…' : 'Reseal'}
                </Button>
              </div>
            )}
          </div>
          {reseal.error && (
            <p className="text-sm text-destructive">
              {reseal.error instanceof Error ? reseal.error.message : 'Reseal failed'}
            </p>
          )}
        </section>

        <section className="border rounded-md p-4 space-y-3">
          <h2 className="text-sm font-semibold">Audit trail</h2>
          {eventsQuery.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : eventsQuery.error ? (
            <p className="text-sm text-destructive">
              {eventsQuery.error instanceof Error
                ? eventsQuery.error.message
                : 'Failed to load events'}
            </p>
          ) : (
            <AuditTrail events={eventsQuery.data?.events ?? []} />
          )}
        </section>
      </div>

      {voidOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="document-sign-void-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        >
          <div className="bg-background border rounded-lg shadow-lg w-full max-w-md p-6 space-y-4">
            <h2 id="document-sign-void-title" className="text-lg font-semibold">
              Void this envelope?
            </h2>
            <p className="text-sm text-muted-foreground">
              Recipients with active signing links will see a "voided" message and won't be able to
              sign. The audit trail keeps everything that's already happened.
            </p>
            <Textarea
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="Reason (required)"
              maxLength={500}
              rows={3}
              aria-label="Void reason"
              autoFocus
            />
            {voidMutation.error && (
              <p className="text-sm text-destructive" role="alert">
                {voidMutation.error instanceof Error
                  ? voidMutation.error.message
                  : 'Failed to void'}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setVoidOpen(false);
                  setVoidReason('');
                }}
                disabled={voidMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  try {
                    await voidMutation.mutateAsync(voidReason.trim());
                    setVoidOpen(false);
                    setVoidReason('');
                  } catch {
                    // surfaced via voidMutation.error
                  }
                }}
                disabled={voidMutation.isPending || voidReason.trim().length === 0}
              >
                {voidMutation.isPending ? 'Voiding…' : 'Void envelope'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
